import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';

// Rows per page until the table has measured how many fit on screen
const DEFAULT_PAGE_SIZE = 10;
// Earlier versions cached pages of employee records here; never read again
const LEGACY_CACHE_KEY = 'employeesPageCache';

const hasActiveFilter = (filters, searchTerm) => {
  // Check if any filters or search are active (with minimum 3 characters for search)
  const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
  const hasActiveFilters = Object.keys(filters).some(key => {
    // Active filter with value '1' is default, so don't count it as active filter
    if (key === 'Active' && filters[key] === '1') return false;
    // For text filters, require at least 3 characters
    if (filters[key] && typeof filters[key] === 'string') {
      return filters[key].trim().length >= 3;
    }
    return filters[key] && filters[key] !== '';
  });
  return Boolean(hasActiveSearch || hasActiveFilters);
};

const filterEmployees = (employees, filters, searchTerm) => {
  let filtered = employees;

  // Apply search term first (searches name, surname, and TC number) - minimum 3 characters
  if (searchTerm && searchTerm.trim().length >= 3) {
    const lowercasedSearch = searchTerm.toLocaleLowerCase('tr-TR');
    filtered = filtered.filter(
      emp =>
        (emp.eName && emp.eName.toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
        (emp.eSurname && emp.eSurname.toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
        (emp.tcNumber && emp.tcNumber.toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch))
    );
  }

  // Then apply individual column filters
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      if (key === 'Active') {
        // Active filter always applies (no minimum length)
        filtered = filtered.filter(item =>
          String(item[key]) === String(filters[key])
        );
      } else {
        // Text filters require minimum 3 characters
        const filterValue = String(filters[key]);
        if (filterValue.trim().length >= 3) {
          filtered = filtered.filter(item =>
            String(item[key])
              ?.toLocaleLowerCase('tr-TR')
              .includes(filterValue.toLocaleLowerCase('tr-TR'))
          );
        }
      }
    }
  });

  // Sort employees alphabetically by name and surname
  return [...filtered].sort((a, b) => {
    const nameA = `${a.eName || ''} ${a.eSurname || ''}`.trim().toLocaleLowerCase('tr-TR');
    const nameB = `${b.eName || ''} ${b.eSurname || ''}`.trim().toLocaleLowerCase('tr-TR');
    return nameA.localeCompare(nameB, 'tr');
  });
};

/**
 * Çalışan Yönetimi listesi. The whole Employees collection is read once (it
 * was already read in full for the head counts); search, column filters and
 * paging then run in the browser. Rows per page come from the table, which
 * reports how many fit on screen.
 */
const useEmployeeDirectory = () => {
  const { t } = useTranslation();
  // Read inside the stable fetcher, so switching language doesn't refetch
  const tRef = useRef(t);
  tRef.current = t;
  const [employees, setEmployees] = useState([]);
  const [statsData, setStatsData] = useState({ total: 0, active: 0, inactive: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filters, setFilters] = useState({ Active: '1' });
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const allDocs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      // Head counts over every document, as before
      setStatsData({
        total: allDocs.length,
        active: allDocs.filter(emp => emp.Active === 1).length,
        inactive: allDocs.filter(emp => emp.Active !== 1).length,
      });
      // The list used to come from orderBy('eName'), which skips documents
      // without that field; `isEmpty` placeholders were never shown either
      setEmployees(allDocs.filter(emp => emp.eName !== undefined && !emp.isEmpty));
      setHasLoaded(true);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error(tRef.current('employees.toast.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_CACHE_KEY);
    } catch (e) {
      // Storage unavailable; nothing to clean up
    }
    fetchEmployees();
  }, [fetchEmployees]);

  const filteredEmployees = useMemo(
    () => filterEmployees(employees, filters, searchTerm),
    [employees, filters, searchTerm]
  );
  const isFilterActive = hasActiveFilter(filters, searchTerm);

  // A new search or filter starts from the first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / pageSize));
  // Stays in range when the list shrinks or fewer rows fit
  const page = Math.min(currentPage, totalPages);
  const pageStart = (page - 1) * pageSize;
  const pageRows = useMemo(
    () => filteredEmployees.slice(pageStart, pageStart + pageSize),
    [filteredEmployees, pageStart, pageSize]
  );

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const goToPage = (target) => {
    if (target >= 1 && target <= totalPages) setCurrentPage(target);
  };

  /** Shows the page holding this employee; false when the list doesn't contain them. */
  const revealEmployee = (employeeId) => {
    const index = filteredEmployees.findIndex((emp) => emp.id === employeeId);
    if (index < 0) return false;
    setCurrentPage(Math.floor(index / pageSize) + 1);
    return true;
  };

  return {
    employees,
    setEmployees,
    filteredEmployees,
    pageRows,
    pageStart,
    filters,
    handleFilter,
    clearFilters,
    searchTerm,
    setSearchTerm,
    isFilterActive,
    isLoading,
    hasLoaded,
    statsData,
    currentPage: page,
    totalPages,
    pageSize,
    setPageSize,
    goToPage,
    revealEmployee,
    refreshEmployees: fetchEmployees,
  };
};

export default useEmployeeDirectory;
